import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { getParents, createInvite } from '../../lib/api/parents';
import { updateProfile, joinHouseholdWithToken } from '../../lib/api/auth';
import { getMyHouseholds } from '../../lib/api/households';
import Button from '../../components/common/Button';
import SafeIcon from '../../common/SafeIcon';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';

export default function Settings() {
  const { user, setUser, switchHousehold } = useAuth();
  const { execute: fetchParents, loading: loadingParents } = useApi(getParents);
  const { execute: execUpdate, loading: updating } = useApi(updateProfile);
  const { execute: execInvite, loading: inviteLoading } = useApi(createInvite);
  const { execute: fetchHouseholds, loading: loadingHouseholds } = useApi(getMyHouseholds);
  
  const [parents, setParents] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [inviteLink, setInviteLink] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [profileData, setProfileData] = useState({ email: user?.email || '', password: '' });

  const loadData = async () => {
    if (user?.id) {
      const hList = await fetchHouseholds(user.id);
      setHouseholds(hList);
    }
    if (user?.householdId) {
      const pList = await fetchParents(user.householdId);
      setParents(pList);
    }
  };

  useEffect(() => {
    loadData();
  }, [user?.householdId]);

  const handleManualJoin = async (e) => {
    e.preventDefault();
    if (!joinCode.trim()) return;
    setJoining(true);
    try {
      // Extract code if they paste the whole URL
      const code = joinCode.includes('/invite/') ? joinCode.split('/invite/')[1] : joinCode.trim();
      const res = await joinHouseholdWithToken(user.id, user.email, code);
      await switchHousehold(res.householdId, res.role);
      setJoinCode('');
      alert("Success! You joined the family.");
      loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setJoining(false);
    }
  };

  const handleSwitch = async (h) => {
    await switchHousehold(h.id, h.role);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const updatedUser = await execUpdate({ 
        email: profileData.email !== user.email ? profileData.email : undefined,
        password: profileData.password || undefined 
      });
      setUser(prev => ({ ...prev, ...updatedUser }));
      alert("Profile updated successfully!");
      setProfileData(prev => ({ ...prev, password: '' }));
    } catch (err) {
      console.error("Update failed", err);
    }
  };

  const handleInvite = async () => {
    try {
      const token = await execInvite(user.householdId, user.id);
      setInviteLink(`${window.location.origin}/#/invite/${token}`);
    } catch (err) {
      console.error("Invite generation failed", err);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in max-w-2xl mx-auto pb-20">
      <header className="mb-8">
        <h1 className="text-3xl font-black text-gray-900">Settings</h1>
        <p className="text-gray-500">Manage your profile and households</p>
      </header>

      {/* Household Switching Section */}
      <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <SafeIcon icon="Home" className="mr-3 text-indigo-600 w-5 h-5" />
          Your Families
        </h2>
        
        <div className="space-y-3 mb-6">
          {loadingHouseholds ? (
            <div className="h-20 bg-gray-50 animate-pulse rounded-xl"></div>
          ) : households.map(h => (
            <button
              key={h.id}
              onClick={() => handleSwitch(h)}
              className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                user?.householdId === h.id 
                ? 'border-indigo-500 bg-indigo-50/50' 
                : 'border-gray-100 bg-gray-50 hover:border-gray-200'
              }`}
            >
              <div className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                  user?.householdId === h.id ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  <SafeIcon icon="Home" className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <p className={`font-bold ${user?.householdId === h.id ? 'text-indigo-900' : 'text-gray-700'}`}>
                    {h.name}
                  </p>
                  <p className="text-xs text-gray-500 uppercase font-bold tracking-widest">{h.role}</p>
                </div>
              </div>
              {user?.householdId === h.id && <SafeIcon icon="Check" className="text-indigo-600 w-5 h-5" />}
            </button>
          ))}
        </div>

        <div className="pt-6 border-t border-gray-100">
          <h3 className="text-sm font-bold text-gray-900 mb-3">Join another family?</h3>
          <form onSubmit={handleManualJoin} className="flex gap-2">
            <input 
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
              placeholder="Paste invite code here..."
              value={joinCode}
              onChange={e => setJoinCode(e.target.value)}
            />
            <Button type="submit" loading={joining} variant="secondary">Join</Button>
          </form>
        </div>
      </section>

      {/* Household Members */}
      <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <SafeIcon icon="Users" className="mr-3 text-indigo-600 w-5 h-5" />
          Parents in Active Family
        </h2>
        <div className="space-y-3 mb-8">
          {parents.map(p => (
            <div key={p.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-bold mr-3">
                  {p.email.charAt(0).toUpperCase()}
                </div>
                <span className="font-semibold text-gray-900 text-sm truncate max-w-[200px]">
                  {p.email} {p.id === user.id && <span className="text-indigo-500 font-normal ml-1">(You)</span>}
                </span>
              </div>
              <span className={`text-[10px] uppercase font-black px-2 py-1 rounded-lg ${p.role === 'admin' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>
                {p.role}
              </span>
            </div>
          ))}
        </div>
        {user?.role === 'admin' && (
          <Button onClick={handleInvite} loading={inviteLoading} icon="UserPlus" variant="primary" className="w-full">
            Invite Another Parent
          </Button>
        )}
      </section>

      {/* Account Security */}
      <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <SafeIcon icon="Shield" className="mr-3 text-indigo-600 w-5 h-5" />
          Account Security
        </h2>
        <form onSubmit={handleUpdateProfile} className="space-y-5">
          <Input label="Email Address" type="email" value={profileData.email} onChange={e => setProfileData({...profileData, email: e.target.value})} />
          <Input label="New Password" type="password" placeholder="Leave blank to keep current" value={profileData.password} onChange={e => setProfileData({...profileData, password: e.target.value})} />
          <Button type="submit" loading={updating} icon="Save">Save Changes</Button>
        </form>
      </section>

      {inviteLink && (
        <Modal title="Invite Link Ready" onClose={() => setInviteLink('')}>
          <p className="text-sm text-gray-600 mb-4">Send this link to the person you want to invite.</p>
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 break-all text-xs font-mono mb-6 text-indigo-600 select-all">
            {inviteLink}
          </div>
          <Button onClick={() => {navigator.clipboard.writeText(inviteLink); alert('Invite link copied!');}} icon="Copy" className="w-full">
            Copy Link
          </Button>
        </Modal>
      )}
    </div>
  );
}